import { service } from '@ember/service'
import Component from '@glimmer/component'
import type { IntlService } from 'ember-intl'
import RouteTemplate from 'ember-route-template'
import type SessionService from 'teacher-ui-client/services/session'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import type AssignmentsProductManageRoute from 'teacher-ui-client/assignments/product/manage/route'
import { PageHeader } from 'teacher-ui-client/components/page-header'
import { UiButtonLinkTo } from 'teacher-ui-client/components/ui-button/link-to'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import { t } from 'ember-intl'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { on } from '@ember/modifier'
import { TabGroupComponent } from 'teacher-ui-client/components/tab-group'
import { themeKey } from 'teacher-ui-client/helpers/theme-key'
import { TAB_TYPE_KEYS } from 'teacher-ui-client/components/tab-group/tab-types'
import { ResetScroll } from 'teacher-ui-client/modifiers/reset-scroll'
import { ContentTile } from 'teacher-ui-client/components/tiles/content'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'

interface Signature {
  Args: {
    model: ModelFor<AssignmentsProductManageRoute>
  }
}
export class AssignmentsManagePage extends Component<Signature> {
  @service intl!: IntlService

  @service session!: SessionService

  @service subscriptionType!: SubscriptionTypeService

  get isSubco() {
    return this.subscriptionType.current && this.session.currentTeacher.subcoFor(this.subscriptionType.current)
  }

  get tabs() {
    const teacherTabs = [
      {
        id: 'current',
        text: this.intl.t('assignments.manage.currentAssignments'),
        routePath: 'assignments.product.manage.current',
      },
      {
        id: 'past',
        text: this.intl.t('assignments.manage.pastAssignments'),
        routePath: 'assignments.product.manage.past',
      },
      {
        id: 'myStudents',
        text: this.intl.t('assignments.manage.myStudents'),
        routePath: 'assignments.product.manage.my-students',
      },
    ]
    const subcoTabs = [
      {
        id: 'allStudents',
        text: this.intl.t('assignments.manage.allStudents'),
        routePath: 'assignments.product.manage.all-students',
      },
    ]
    return this.isSubco ? [...teacherTabs, ...subcoTabs] : teacherTabs
  }

  print = () => {
    window.print()
  }

  <template>
    <div class="m-0 w-full grow">
      <PageHeader>
        <:title>
          <h1>
            {{t "assignments.manage.title"}}
          </h1>
        </:title>
      </PageHeader>

      <div class="mt-6">
        <div class="float-right print:hidden">
          <UiButtonLinkTo @route="assignments.product.create" @appearance={{ButtonAppearance.regular}}>
            <PhosphorIcon @icon="phosphor-plus" class="mr-2" />
            {{t "assignments.create.title"}}
          </UiButtonLinkTo>
          <UiButton {{on "click" this.print}}>
            <PhosphorIcon @icon="phosphor-printer-fill" class="mr-2" />
            {{t "print"}}
          </UiButton>
        </div>
        <TabGroupComponent
          @theme={{themeKey this.subscriptionType.current}}
          @tabType={{TAB_TYPE_KEYS.shadow}}
          data-test-tab-group
          as |tabGroup|
        >
          {{#each this.tabs as |tab|}}
            <tabGroup.linkToTab
              {{ResetScroll onClick=true}}
              data-test-assignment-tab={{tab.id}}
              @route={{tab.routePath}}
            >
              {{tab.text}}
            </tabGroup.linkToTab>
          {{/each}}
        </TabGroupComponent>
      </div>
      <ContentTile class="relative overflow-x-auto rounded-tl-none print:border-none">
        {{outlet}}
      </ContentTile>
    </div>
  </template>
}

export default RouteTemplate(AssignmentsManagePage)
